// Live's actions on a My Generations row (issue 252 item 5, design-ref/gaps/35, LEFTOVERS G4):
// "Save generation", "Download generation", "Save to project" and "Delete generation", as small
// icons at the right of the row's header, in live's order. A talking head's row acts on every
// actor's job (issue 238), as live's on its one generation.
import { ActionIcon, Group, Modal, Text, Tooltip } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { Download, FolderOpen, Save, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import { downloadSignedFile } from "../../api/download";
import { ApiError } from "../../api/errors";
import { deleteJob, getListJobsQueryKey } from "../../api/generated/endpoints/generation/generation";
import type { GenerationOutput, Job } from "../../api/generated/model";
import { ErrorAlert } from "../ErrorAlert";
import { FolderPicker } from "../folders/FolderPicker";
import { outputFileName } from "./file-names";
import { deliveredOutputs, outputKey, type OutputPick } from "./output-picks";
import { SaveName } from "./SaveName";
import { useSaveOutputs, type SaveOptions } from "./SaveOutput";
import { isSettled } from "./status";

export interface RowActionsProps {
  teamId: string;
  brandId: string;
  /** The row's jobs: one, or a talking head's (`groupGenerations`). */
  jobs: readonly [Job, ...Job[]];
  /** Reads the list again, for fresh URLs after a download's link expired. */
  readAgain: () => void;
  /** Live's "Save to project": the Studio's rows have it, the Sounds page's do not. */
  toProject?: boolean;
  /** A download's file name, where a page names its own (live's `sound-<id>`). */
  fileName?: (job: Job, output: GenerationOutput, number: number | undefined) => string;
  /**
   * Live's Sounds rows: "Save generation" asks for a name first ("Add name"), and stays grey
   * once every output is kept, as live's saved generation.
   */
  askName?: boolean;
}

/**
 * The row's icons (`icons`, for its header), what they did (`notes`, under the header) and the
 * outputs its saves kept (`saved`, by `outputKey`, for the cards). Save generation and Save to
 * project keep every delivered output of the row's jobs, Save to project in the project chosen
 * in live's picker; Download generation saves each file, a talking head's numbered across its
 * jobs; Delete generation hides the row's jobs for good with no question asked, as live's, once
 * all have settled and only for their maker (spec G4 "Delete": the server says no to anyone
 * else, and the row shows why).
 */
export function useRowActions(props: RowActionsProps) {
  const { teamId, brandId, jobs, readAgain, toProject = true, askName = false } = props;
  const nameOf = props.fileName ?? outputFileName;
  const queryClient = useQueryClient();
  const router = useRouter();
  const saving = useSaveOutputs({ teamId, brandId });
  const delivered = jobs.flatMap((job) => deliveredOutputs(job).map((output) => ({ job, output })));
  const picks = delivered.map(({ job, output }) => ({ jobId: job.id, index: output.index }));
  // The outputs live's "Save to project" keeps, while its picker is open: the row's or a card's.
  const [picking, setPicking] = useState<readonly OutputPick[] | null>(null);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const remove = useMutation({
    mutationFn: async () => {
      for (const job of jobs) {
        try {
          await deleteJob(teamId, job.id);
        } catch (error) {
          // Gone already (another tab's delete): the row's other jobs still go.
          if (!(error instanceof ApiError && error.status === 404)) throw error;
        }
      }
    },
    // A refusal part way leaves the rest, so the list is read again either way.
    onSettled: () => queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) }),
  });
  // A talking head's files are numbered across its jobs when the row downloads them all.
  const download = async (files: readonly { job: Job; output: GenerationOutput }[], numbered: boolean) => {
    setDownloadFailed(false);
    for (const [at, { job, output }] of files.entries()) {
      try {
        const number = numbered ? at + 1 : undefined;
        await downloadSignedFile(output.url!, nameOf(job, output, number));
      } catch {
        // An expired link is fresh after the read; the other files still download.
        setDownloadFailed(true);
        readAgain();
      }
    }
  };
  const icon = (label: string, child: ReactNode, onClick: () => void, disabled: boolean) => (
    <Tooltip label={label}>
      <ActionIcon
        variant="subtle"
        color="gray"
        size="sm"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        {child}
      </ActionIcon>
    </Tooltip>
  );
  const none = picks.length === 0;
  // Live's project picker, shared by the row's and a card's "Save to project".
  const picker = (
    <Modal
      opened={picking !== null}
      onClose={() => setPicking(null)}
      title="Save to project"
      centered
    >
      <FolderPicker
        teamId={teamId}
        brandId={brandId}
        submitLabel="Save"
        pending={saving.saving}
        onSubmit={(folderId) =>
          saving.save(picking ?? [], { folderId, onSaved: () => setPicking(null) })
        }
        onCancel={() => setPicking(null)}
      />
      {saving.failed ? saving.result : null}
    </Modal>
  );
  const unsettled = jobs.some((job) => !isSettled(job.status));
  const icons = (
    <Group gap={4} wrap="nowrap">
      {askName ? (
        <SaveName
          label="Save generation"
          icon={<Save size={16} />}
          disabled={none || saving.saving || picks.every((pick) => saving.saved.has(outputKey(pick)))}
          saving={saving.saving}
          onSave={(name, done) => saving.save(picks, { name, onSaved: done })}
        />
      ) : (
        icon("Save generation", <Save size={16} />, () => saving.save(picks), none || saving.saving)
      )}
      {icon("Download generation", <Download size={16} />, () => void download(delivered, jobs.length > 1), none)}
      {toProject
        ? icon(
            "Save to project",
            <FolderOpen size={16} />,
            () => setPicking(picks),
            none || saving.saving,
          )
        : null}
      {icon(
        "Delete generation",
        <Trash2 size={16} />,
        () => remove.mutate(),
        unsettled || remove.isPending,
      )}
      {picker}
    </Group>
  );
  const said = saving.result !== null || downloadFailed || remove.isError;
  const notes = said ? (
    <div>
      {saving.result}
      {downloadFailed ? (
        <Text size="sm" role="alert">
          A file could not be downloaded; its link is fresh now, so try again.
        </Text>
      ) : null}
      {remove.isError ? <ErrorAlert error={remove.error} /> : null}
    </div>
  ) : null;
  return {
    icons,
    notes,
    saved: saving.saved,
    /** Keeps one output, as a card's Save does, with `options`; its line shows under the header. */
    saveOne: (pick: OutputPick, options: SaveOptions = {}) => saving.save([pick], options),
    /** A card's "Save to project": live's picker, then the one output kept in the project. */
    saveToProject: (pick: OutputPick) => setPicking([pick]),
    /** A card's "Download": its one file, under the row's names. */
    downloadOne: (job: Job, output: GenerationOutput) => void download([{ job, output }], false),
    /**
     * Beyond live (Daniil, 2026-10-26, "Save first, then open"; spec open-from-studio): a delivered
     * video opened in the editor in a new tab, on its library file. A saved output opens its
     * file's own project, found or made, as the library's item does; one not saved yet is kept
     * first, as the card's Save keeps it (the brand's root, its default name), then opened. The
     * tab opens with the press, so no pop-up blocker refuses it, and closes if the save is refused.
     */
    openInEditor: (job: Job, output: GenerationOutput) => {
      const editor = (assetId: string) =>
        router.buildLocation({
          to: "/teams/$teamId/brands/$brandId/editor/asset/$assetId",
          params: { teamId, brandId, assetId },
        }).href;
      const savedId = output.saved_asset_id ?? null;
      if (savedId !== null) {
        window.open(editor(savedId), "_blank", "noopener,noreferrer");
        return;
      }
      const tab = window.open("", "_blank");
      // Nothing there needs this page: the link back goes, as noopener's would.
      if (tab !== null) tab.opener = null;
      saving.save([{ jobId: job.id, index: output.index }], {
        onSaved: ([asset]) => {
          if (tab === null) return;
          if (asset === undefined) tab.close();
          else tab.location.href = editor(asset.id);
        },
        onFailed: () => tab?.close(),
      });
    },
    saving: saving.saving,
    /**
     * For a view that shows its own buttons, not `icons` (the Studio's preview): the project
     * picker to render, and "Delete generation", off until every job has settled.
     */
    picker,
    deleteGeneration: () => remove.mutate(),
    deletable: !unsettled && !remove.isPending,
  };
}
